import { Tooltip } from '@material-ui/core';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { toastError } from '@components/Toast';

import { currentStageColumns } from '@features/crm/utils/helpers';

import { storeTotalBrandsCount } from '@store/actions/crm/CRMCommonAction';
import { clearGlobalFilter } from '@store/actions/FilterAction';
import { closeLoader, openLoader } from '@store/actions/LoaderAction';

import ApiClient from '@lib/apiClient';

import {
	_getKey,
	changeDateFormat,
	convertNumberToUSFormat,
	getDaysAgo,
	getNumberUnit,
	isCRMAdmin,
	isCRMManagement,
	isPageReachBottom
} from '@utils/Util';

const BrandList = () => {
	const [brands, setBrands] = useState([]);
	const filterData = useSelector((store) => store.filterFieldStore);
	const loader = useSelector((store) => store.loaderStore);
	const [counts, setCounts] = useState(null);
	const [pageNumber, setPageNumber] = useState(0);
	const [activeTab, setActiveTab] = useState(currentStageColumns[0].value);
	const dispatch = useDispatch();
	const brandsRef = useRef();
	const location = useLocation();

	const generateParams = (pageNumber) => {
		let params = `?page=${pageNumber}&status=${activeTab}`;
		if (filterData.brand) {
			params += `&parentId=${filterData.brand[0].key}`;
		}
		if (filterData.brandType) {
			params += `&brandType=${filterData.brandType[0].key}`;
		}
		if (filterData.name) {
			params += `&name=${filterData.name[0].key}`;
		}
		if (filterData.sort) {
			params += `&sort=${filterData.sort[0].key}`;
		} else {
			params += `&sort=statusUpdatedAt,desc`;
		}
		return params;
	};

	const fetchBrands = async (page = 0) => {
		try {
			dispatch(openLoader());
			const { data } = await ApiClient.GET('getCRMBrandList', generateParams(page));
			dispatch(storeTotalBrandsCount(data.totalElements));
			let tmpData = [];
			if (page === 0) {
				tmpData = data.data;
				brandsRef.current = data;
			} else {
				tmpData = [...brands, ...data.data];
				brandsRef.current = {
					...data,
					data: [...brandsRef.current.data, ...data.data]
				};
			}
			if (page === 0) {
				setPageNumber(0);
			}
			setBrands(tmpData);
			dispatch(closeLoader());
		} catch (err) {
			dispatch(closeLoader());
			toastError(err.response.data.message);
		}
	};

	const fetchBrandWiseCount = async () => {
		try {
			const { data } = await ApiClient.GET('brandWiseCount');
			setCounts(data);
		} catch (err) {
			toastError(err.response.data.message);
		}
	};

	useEffect(() => {
		dispatch(clearGlobalFilter());
		window.addEventListener('scroll', handleScroll);
		fetchBrandWiseCount();
		return () => window.removeEventListener('scroll', handleScroll);
	}, []);

	useEffect(() => {
		window.scrollTo(0, 0);
		fetchBrands(0);
	}, [filterData, activeTab]);

	useEffect(() => {
		if (pageNumber !== 0) {
			fetchBrands(pageNumber);
		}
	}, [pageNumber]);

	const handleScroll = async () => {
		if (isPageReachBottom()) {
			let { totalElements, currentPage, data } = brandsRef.current;
			if (totalElements > 0 && totalElements !== data.length) {
				setPageNumber(currentPage + 1);
			}
		}
	};

	const generateNameFromList = (dataList) => {
		if (!dataList) return;
		let nameList = [];
		dataList.forEach((item) => {
			nameList.push(item.name);
		});
		return nameList.join(', ');
	};

	const renderTableBody = () => {
		return brands.map((item) => {
			return (
				<tr
					key={`brand_id_${_getKey()}`}
					onClick={() => window.open(`/brand/profile/${item.id}`, '_blank')}
				>
					<td>
						<div className="d-flex align-items-center">
							<div className="logo">
								{item.brandLogoDocResponse?.docUrl && (
									<img
										src={item.brandLogoDocResponse?.docUrl}
										alt="brand-logo"
										className="logo"
										style={{ width: '100px', height: 'auto', objectFit: 'contain' }}
									/>
								)}
							</div>
							<div className="name-location ml-2">
								<p className="regular-14 weight-500 gray_dark_01">
									{item.name}{' '}
									{item?.hasAnyRunningActivity && (
										<Tooltip title="Running" placement="top" arrow>
											<img
												src={`${process.env.PUBLIC_URL}/icons/waves.svg`}
												alt="wave"
												className="ml-12"
											/>
										</Tooltip>
									)}
								</p>
								{item?.locationResponseList?.[0] && (
									<p className="regular-12 gray_dark_02">
										{item?.locationResponseList?.[0]?.city},{' '}
										{item?.locationResponseList?.[0]?.countryResponse?.name}
									</p>
								)}
							</div>
						</div>
					</td>
					<td>
						<p className="regular-14 gray_dark_02">{item.group}</p>
						<p className="regular-14 gray_dark_02">{item.type}</p>
					</td>
					{activeTab === 'ON_BOARDING' && (
						<td>
							<p className="regular-14 gray_dark_02">{item.currentOnBoardingStage}</p>
						</td>
					)}
					<td>
						<p className="regular-14 gray_dark_02 mb-1">
							Target:
							<span className="gray_dark_01">
								{item?.targetAmount > 0 ? ` $${getNumberUnit(item?.targetAmount)} ` : 'N/A'}
							</span>
						</p>
						<p className="regular-14 gray_dark_02 mb-1">
							Achievement:
							<span className="gray_dark_01">
								{item?.achievedAmount > 0
									? ` $${getNumberUnit(item?.achievedAmount)} `
									: 'N/A'}
							</span>
						</p>
						<p className="regular-14 gray_dark_02">
							Opportunity:
							<span className="gray_dark_01">
								{item?.targetAmount - item?.achievedAmount > 0
									? ` $${getNumberUnit(item?.targetAmount - item?.achievedAmount)} `
									: 'N/A'}
							</span>
						</p>
					</td>
					<td>
						<p className="regular-14 gray_dark_02 mb-1">
							A/C:{' '}
							<span className="gray_dark_01">
								{generateNameFromList(item.accountManagerList)}
							</span>
						</p>
						<p className="regular-14 gray_dark_02 mb-1">
							PM:{' '}
							<span className="gray_dark_01">
								{generateNameFromList(item.projectManagerList)}
							</span>
						</p>
						<p className="regular-14 gray_dark_02 mb-1">
							MR:{' '}
							<span className="gray_dark_01">
								{generateNameFromList(item.merchandiserList)}
							</span>
						</p>
						<p className="regular-14 gray_dark_02">
							BYR: <span className="gray_dark_01">{generateNameFromList(item.buyerList)}</span>
						</p>
					</td>
					{['ACTIVE', 'ARCHIVE'].includes(activeTab) && (
						<td>
							<p className="regular-14 gray_dark_02 mb-1">
								Total Styles:
								<span className="gray_dark_01">
									&nbsp;{getNumberUnit(item?.nitexDesign + item?.buyerDesign, true)}
								</span>
							</p>
							<p className="regular-14 gray_dark_02 mb-1">
								Nitex Styles:
								<span className="gray_dark_01">
									{' '}
									{getNumberUnit(item?.nitexDesign, true)}
								</span>
							</p>
							<p className="regular-14 gray_dark_02">
								Total Qty:
								<span className="gray_dark_01">
									{' '}
									{getNumberUnit(item?.totalQuantity, true)}
								</span>
							</p>
						</td>
					)}
					{!['ON_BOARDED', 'ON_BOARDING', 'PIPELINE'].includes(activeTab) && (
						<td>
							{item.firstOrderDate && (
								<p className="regular-14 gray_dark_02 mb-2">
									{changeDateFormat(item.firstOrderDate, 'YYYY-MM-DD', 'DD MMM, YYYY')}{' '}
									<span className="gray_dark_03 d-block">
										({getDaysAgo(item.firstOrderDate, 'YYYY-MM-DD')})
									</span>
								</p>
							)}
							{item.lastOrderDate && (
								<p className="regular-14 gray_dark_01">
									{changeDateFormat(item.lastOrderDate, 'YYYY-MM-DD', 'DD MMM, YYYY')}{' '}
									<span className="gray_dark_03 d-block">
										({getDaysAgo(item.lastOrderDate, 'YYYY-MM-DD')})
									</span>{' '}
								</p>
							)}
						</td>
					)}
					{activeTab === 'PIPELINE' && <td />}
					<td>
						<p className="regular-14 gray_dark_02">
							{item?.marketValue > 0 ? ` $${getNumberUnit(item?.marketValue)} ` : ''}
						</p>
					</td>
					<td>
						<p className="regular-14 gray_dark_02">
							{item?.annualProduction > 0 ? ` $${getNumberUnit(item?.annualProduction)} ` : ''}
						</p>
					</td>
					<td>
						<p className="regular-14 gray_dark_02">
							{item?.noOfOutlet > 0
								? ` ${convertNumberToUSFormat(item?.noOfOutlet, true)} `
								: ''}
						</p>
					</td>
				</tr>
			);
		});
	};

	const onTabChange = (value) => {
		setActiveTab(value);
	};

	const renderTabs = () => {
		let tmpCurrentStages = currentStageColumns;
		if (isCRMAdmin()) {
			tmpCurrentStages = [...currentStageColumns, { label: 'Pending', value: 'PENDING' }];
		}
		return tmpCurrentStages?.map((item) => {
			if (item.value !== 'ARCHIVE' || (item.value === 'ARCHIVE' && isCRMManagement())) {
				return (
					<li
						key={`brand_tab_${item.value}`}
						onClick={() => onTabChange(item.value)}
						className={activeTab === item.value ? 'active' : ''}
					>
						{item.label} ({counts?.[item.value] ? counts?.[item.value] : '0'})
					</li>
				);
			}
		});
	};

	return (
		<section className="body-content pt-0">
			<div className="brand-overview-with-tab">
				<div className="brand-tab-container">
					<div
						className={`brandlist-inner-tabs inner-tabs pb-2 mb-1 bg-gray ${
							location.pathname === '/brands/list' ? 'on-brand-list' : ''
						}`}
					>
						<ul>{renderTabs()}</ul>
					</div>

					<div className="scroll-x-label">
						<table className="table with-border brand-profile-list-table">
							<thead>
								<tr>
									<th>Brand name</th>
									<th>Type</th>
									{activeTab === 'ON_BOARDING' && <th>On-boarding Status</th>}
									<th>Business overview</th>
									<th>Team &amp; buyer Info</th>
									{!['ON_BOARDED', 'ON_BOARDING', 'PIPELINE', 'PENDING'].includes(
										activeTab
									) && <th>Style overview </th>}
									{!['ON_BOARDED', 'ON_BOARDING', 'PIPELINE'].includes(activeTab) && (
										<th>First &amp; Last order</th>
									)}
									{activeTab === 'PIPELINE' && <th>Running Activities</th>}
									<th>Market value ($)</th>
									<th>APN ($)</th>
									<th>Stores world wide</th>
								</tr>
							</thead>
							<tbody>{renderTableBody()}</tbody>
						</table>
						{!loader && brands.length === 0 && (
							<p className="alert common-empty-state">No brand found</p>
						)}
					</div>
				</div>
			</div>
		</section>
	);
};

export default BrandList;
